import { type Actor, can, type Role } from '@veem/crm-schema'

export type AssistantTool =
  | 'my_open_tasks'
  | 'find_order'
  | 'overdue_finance'
  | 'material_stock'
  | 'propose_task'

export type StarterIcon = 'tasks' | 'steps' | 'order' | 'stock' | 'invoice' | 'payment' | 'draft'

export interface AssistantStarter {
  id: string
  title: string
  description: string
  prompt: string
  icon: StarterIcon
  tool: AssistantTool
  mode: 'send' | 'fill'
}

export const canUseAssistantTool = (actor: Actor | null | undefined, tool: AssistantTool) => {
  switch (tool) {
    case 'my_open_tasks':
      return can(actor, 'read', 'tasks')
    case 'find_order':
      return can(actor, 'read', 'orders') || can(actor, 'read', 'production')
    case 'overdue_finance':
      return can(actor, 'read', 'invoices') || can(actor, 'read', 'paymentRequests')
    case 'material_stock':
      return can(actor, 'read', 'warehouse')
    case 'propose_task':
      return can(actor, 'create', 'tasks')
  }
}

const actions = {
  tasks: {
    title: 'Мої відкриті завдання',
    description: 'Що потребує моєї уваги',
    prompt: 'Покажи мої відкриті завдання з термінами. Виділи прострочені.',
    icon: 'tasks',
    tool: 'my_open_tasks',
    mode: 'send',
  },
  steps: {
    title: 'Мої системні кроки',
    description: 'Дії, на які чекає робочий процес',
    prompt: 'Покажи мої системні кроки. З чого варто почати сьогодні?',
    icon: 'steps',
    tool: 'my_open_tasks',
    mode: 'send',
  },
  next: {
    title: 'Що в мене далі',
    description: 'Найближчі завдання та етапи',
    prompt: 'Перевір мої відкриті завдання та системні кроки. Що в мене далі за термінами?',
    icon: 'steps',
    tool: 'my_open_tasks',
    mode: 'send',
  },
  today: {
    title: 'Завдання на сьогодні',
    description: 'Мої завдання з терміном на сьогодні',
    prompt: 'Які мої відкриті завдання мають термін сьогодні? Окремо вкажи прострочені.',
    icon: 'tasks',
    tool: 'my_open_tasks',
    mode: 'send',
  },
  handoff: {
    title: 'Замовлення до передачі',
    description: 'Мої кроки передачі у виробництво',
    prompt: 'Покажи серед моїх системних кроків замовлення, які потрібно передати у виробництво.',
    icon: 'steps',
    tool: 'my_open_tasks',
    mode: 'send',
  },
  order: {
    title: 'Статус замовлення…',
    description: 'Етап, термін і стан виробництва',
    prompt: 'Перевір статус замовлення [номер або назва]. Покажи етап, термін і стан виробництва.',
    icon: 'order',
    tool: 'find_order',
    mode: 'fill',
  },
  shortage: {
    title: 'Матеріали для замовлення…',
    description: 'Перевірити стан забезпечення',
    prompt:
      'Знайди замовлення [номер або назва] і перевір його стан забезпечення матеріалами. Використай лише доступні дані.',
    icon: 'stock',
    tool: 'find_order',
    mode: 'fill',
  },
  stock: {
    title: 'Залишок матеріалу…',
    description: 'Фізичний, резерв і доступний залишок',
    prompt:
      'Покажи залишок матеріалу [назва матеріалу]: фізичний, зарезервований і доступний, з одиницями виміру.',
    icon: 'stock',
    tool: 'material_stock',
    mode: 'fill',
  },
  emptyStock: {
    title: 'Нульові залишки',
    description: 'Перевірити доступні залишки матеріалів',
    prompt:
      'Перевір залишки матеріалів і покажи ті, де доступний залишок нульовий або від’ємний. Зазнач, якщо перелік обмежений результатом пошуку.',
    icon: 'stock',
    tool: 'material_stock',
    mode: 'send',
  },
  invoices: {
    title: 'Прострочені рахунки',
    description: 'Клієнти, суми й терміни оплати',
    prompt: 'Покажи доступні мені прострочені рахунки клієнтів, залишок до сплати та терміни.',
    icon: 'invoice',
    tool: 'overdue_finance',
    mode: 'send',
  },
  payments: {
    title: 'Неоплачені заявки',
    description: 'Платежі на погодженні та до сплати',
    prompt:
      'Покажи доступні мені неоплачені заявки на оплату: постачальника, суму, термін і статус.',
    icon: 'payment',
    tool: 'overdue_finance',
    mode: 'send',
  },
  draft: {
    title: 'Створити завдання…',
    description: 'Чернетка для мого підтвердження',
    prompt:
      'Підготуй чернетку завдання для мене: [що потрібно зробити]. Термін: [дата або без терміну].',
    icon: 'draft',
    tool: 'propose_task',
    mode: 'fill',
  },
} satisfies Record<string, Omit<AssistantStarter, 'id'>>

type ActionId = keyof typeof actions
const roleActions: Record<Role, ActionId[]> = {
  admin: ['tasks', 'steps', 'invoices', 'payments', 'order', 'draft'],
  sales: ['tasks', 'handoff', 'order', 'draft'],
  engineer: ['tasks', 'steps', 'order', 'stock', 'draft'],
  chief_engineer: ['tasks', 'steps', 'order', 'stock', 'draft'],
  production_head: ['steps', 'tasks', 'shortage', 'stock', 'order', 'draft'],
  cnc_head: ['steps', 'tasks', 'stock', 'order', 'draft'],
  production: ['steps', 'tasks', 'shortage', 'stock', 'order', 'draft'],
  storekeeper: ['stock', 'emptyStock', 'steps', 'tasks', 'draft'],
  logistician: ['steps', 'tasks', 'order', 'draft'],
  accountant: ['invoices', 'payments', 'tasks', 'steps', 'order', 'draft'],
  worker: ['next', 'today', 'steps', 'draft'],
}

export const assistantQuickActions = (actor: Actor): AssistantStarter[] =>
  roleActions[actor.role]
    .map((id) => ({ id, ...actions[id] }))
    .filter((starter) => canUseAssistantTool(actor, starter.tool))

export const assistantTemplates = (actor: Actor): AssistantStarter[] => {
  const templates: AssistantStarter[] = [
    {
      id: 'day-summary',
      title: 'Підсумок дня для керівника',
      description: 'Зроблено, перешкоди та наступні кроки',
      prompt:
        'Підготуй короткий підсумок дня для керівника. Зроблено: [результати дня]. Перешкоди: [що заважає]. Наступні кроки: [план]. Не додавай вигаданих фактів.',
      icon: 'tasks',
      tool: 'my_open_tasks',
      mode: 'fill',
    },
    {
      id: 'task-template',
      title: can(actor, 'assign', 'tasks') ? 'Завдання для колеги' : 'Завдання для себе',
      description: 'Опис, виконавець і термін',
      prompt: can(actor, 'assign', 'tasks')
        ? 'Підготуй чернетку завдання: [що потрібно зробити]. Виконавець: [ім’я працівника]. Термін: [дата]. Уточни виконавця, якщо не знайдеш його однозначно.'
        : 'Підготуй чернетку завдання для мене: [що потрібно зробити]. Термін: [дата].',
      icon: 'draft',
      tool: 'propose_task',
      mode: 'fill',
    },
    can(actor, 'read', 'customers')
      ? {
          id: 'delay-letter',
          title: 'Лист клієнту про затримку',
          description: 'Ввічлива чернетка з новим терміном',
          prompt:
            'Підготуй чернетку листа клієнту [ім’я або компанія] про затримку замовлення [номер]. Причина: [причина]. Новий термін: [дата]. Лише текст листа, без надсилання.',
          icon: 'order',
          tool: 'my_open_tasks',
          mode: 'fill',
        }
      : {
          id: 'handover-note',
          title: 'Передати роботу колезі',
          description: 'Стан роботи та важливі деталі',
          prompt:
            'Підготуй коротку записку для передачі роботи. Робота: [назва]. Поточний стан: [що зроблено]. Залишилось: [наступні кроки]. Важливі деталі: [уточнення].',
          icon: 'steps',
          tool: 'my_open_tasks',
          mode: 'fill',
        },
  ]
  return templates.filter((starter) => canUseAssistantTool(actor, starter.tool))
}

export const firstPromptPlaceholder = (prompt: string): [number, number] | null => {
  const match = /\[[^\]]+\]/u.exec(prompt)
  return match ? [match.index, match.index + match[0].length] : null
}
